import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AudioLines,
  Loader2,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  PhoneOff,
  Plus,
  Trash2,
} from "lucide-react";
import AiConfigCollapsibleCard from "./AiConfigCollapsibleCard";

const API = process.env.REACT_APP_BACKEND_URL;

export const DEFAULT_REALTIME_MODEL = "gpt-realtime-2.1-mini";
const BUILTIN_REALTIME_MODELS = ["gpt-realtime-2.1-mini", "gpt-realtime-2.1"];
const PRICING_COLUMNS = [
  { key: "audio_input", label: "Audio in" },
  { key: "audio_input_cached", label: "Audio in cache" },
  { key: "audio_output", label: "Audio out" },
  { key: "text_input", label: "Testo in" },
  { key: "text_input_cached", label: "Testo in cache" },
  { key: "text_output", label: "Testo out" },
];
const VOICE_LABELS = {
  marin: "Marin (consigliata)",
  cedar: "Cedar (consigliata)",
};
const EFFORT_LABELS = { minimal: "Minimo", low: "Basso", medium: "Medio", high: "Alto" };
const END_REASON_LABELS = {
  user: "Chiusa dall'utente",
  max_duration: "Durata massima",
  feature_disabled: "Funzione disattivata",
  voice_budget_exceeded: "Budget esaurito",
  admin: "Terminata da admin",
  replaced: "Sostituita da nuova sessione",
  abandoned: "Abbandonata",
  page_closed: "Pagina chiusa",
  error: "Errore",
};
const ACTION_STATUS_LABELS = {
  pending: "In attesa",
  confirmed: "Confermata",
  executed: "Eseguita",
  failed: "Fallita",
  cancelled: "Annullata",
  expired: "Scaduta",
};

const token = () => localStorage.getItem("super_admin_token");
const headers = (json = false) => ({
  Authorization: `Bearer ${token()}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const fmtUsd = (v, digits = 4) => `$${(Number(v) || 0).toFixed(digits)}`;
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};
const fmtDuration = (seconds) => {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
const currentMonth = () => new Date().toISOString().slice(0, 7);

/** Converte la config vocale del backend nello stato del form admin. */
export function voiceConfigFromApi(raw) {
  const v = raw || {};
  return {
    enabled: !!v.enabled,
    model: v.model || DEFAULT_REALTIME_MODEL,
    voice: v.voice || "marin",
    max_session_minutes: v.max_session_minutes ?? 15,
    monthly_budget_usd: v.monthly_budget_usd ?? "",
    per_user_monthly_minutes: v.per_user_monthly_minutes ?? "",
    reasoning_effort: v.reasoning_effort || "",
    show_cost_to_user: v.show_cost_to_user !== false,
    pricing: Object.fromEntries(
      Object.entries(v.pricing || {}).map(([model, rates]) => [
        model,
        Object.fromEntries(PRICING_COLUMNS.map(({ key }) => [key, rates?.[key] ?? ""])),
      ])
    ),
  };
}

/** Payload PUT /api/admin/ai-config per la chat vocale. */
export function voiceConfigToPayload(value) {
  const num = (x) => (x === "" || x === null || x === undefined ? null : Number(x));
  const pricing = {};
  Object.entries(value.pricing || {}).forEach(([model, rates]) => {
    const clean = {};
    PRICING_COLUMNS.forEach(({ key }) => {
      const n = num(rates?.[key]);
      if (n !== null && Number.isFinite(n)) clean[key] = n;
    });
    if (model.trim()) pricing[model.trim()] = clean;
  });
  return {
    enabled: value.enabled,
    model: (value.model || DEFAULT_REALTIME_MODEL).trim(),
    voice: value.voice || null,
    max_session_minutes: num(value.max_session_minutes),
    monthly_budget_usd: num(value.monthly_budget_usd),
    per_user_monthly_minutes: num(value.per_user_monthly_minutes),
    reasoning_effort: value.reasoning_effort || null,
    show_cost_to_user: !!value.show_cost_to_user,
    pricing,
  };
}

/** Sezione «Chat vocale» della tab Configurazione (Admin → Intelligenza artificiale). */
export function AssistantVoiceConfigCard({ value, onChange, disabled, assistantEnabled, voices, efforts }) {
  const [newModel, setNewModel] = useState("");
  const pricingModels = Object.keys(value.pricing || {});
  const modelOptions = Array.from(new Set([...BUILTIN_REALTIME_MODELS, ...pricingModels]));
  const isCustomModel = !modelOptions.includes(value.model);
  const [customMode, setCustomMode] = useState(isCustomModel);
  const voiceOptions = voices?.length ? voices : ["marin", "cedar"];
  const effortOptions = efforts?.length ? efforts : ["minimal", "low", "medium", "high"];
  const selectedHasPricing = pricingModels.includes(value.model)
    && PRICING_COLUMNS.every(({ key }) => value.pricing[value.model]?.[key] !== "" && value.pricing[value.model]?.[key] !== undefined);
  const off = disabled || !value.enabled;

  const set = (patch) => onChange({ ...value, ...patch });
  const setRate = (model, key, rate) => {
    set({ pricing: { ...value.pricing, [model]: { ...(value.pricing[model] || {}), [key]: rate } } });
  };
  const addModel = (model) => {
    const id = model.trim();
    if (!id || value.pricing[id]) return;
    set({ pricing: { ...value.pricing, [id]: Object.fromEntries(PRICING_COLUMNS.map(({ key }) => [key, ""])) } });
  };
  const removeModel = (model) => {
    const next = { ...value.pricing };
    delete next[model];
    set({ pricing: next });
  };

  return (
    <AiConfigCollapsibleCard
      title="Chat vocale (OpenAI Realtime)"
      icon={<AudioLines className="w-4 h-4 text-teal-600" />}
      description="Conversazione a voce nel pannello laterale dell'assistente, presentata all'utente come EVA, senza trascrizioni. Disattivata di default: oltre a questo interruttore serve l'abilitazione per singolo utente (Utenti → dettaglio → Assistente vocale, oppure tab Chat vocale)."
      statusLabel={value.enabled ? "Attiva" : "Spenta"}
      statusActive={value.enabled && !disabled}
      className={disabled ? "opacity-60" : ""}
      contentClassName="space-y-5"
    >
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div>
            <Label>Abilita chat vocale</Label>
            <p className="text-xs text-slate-500">
              {assistantEnabled ? "Richiede anche il flag sull'utente." : "Attiva prima l'assistente chat testuale."}
            </p>
          </div>
          <Switch
            checked={value.enabled}
            disabled={disabled}
            onCheckedChange={(v) => set({ enabled: v })}
            data-testid="ai-voice-enabled"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Modello Realtime</Label>
            <Select
              value={customMode ? "__custom__" : value.model}
              onValueChange={(v) => {
                if (v === "__custom__") {
                  setCustomMode(true);
                  return;
                }
                setCustomMode(false);
                set({ model: v });
              }}
              disabled={disabled}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {modelOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}{m === DEFAULT_REALTIME_MODEL ? " (predefinito)" : ""}
                  </SelectItem>
                ))}
                <SelectItem value="__custom__">Modello personalizzato…</SelectItem>
              </SelectContent>
            </Select>
            {customMode && (
              <Input
                className="mt-2 font-mono text-sm"
                placeholder="es. gpt-realtime-2.2"
                value={value.model}
                disabled={disabled}
                onChange={(e) => set({ model: e.target.value.trim() })}
              />
            )}
            {!selectedHasPricing && value.model && (
              <p className="text-xs text-rose-600 mt-1">
                Aggiungi le tariffe di «{value.model}» nella tabella qui sotto: senza tariffe il salvataggio viene rifiutato.
              </p>
            )}
          </div>
          <div>
            <Label>Voce</Label>
            <Select value={value.voice} onValueChange={(v) => set({ voice: v })} disabled={disabled}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {voiceOptions.map((v) => (
                  <SelectItem key={v} value={v}>{VOICE_LABELS[v] || v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Durata massima sessione (minuti, max 60)</Label>
            <Input
              type="number"
              min="1"
              max="60"
              className="mt-1"
              value={value.max_session_minutes}
              disabled={disabled}
              onChange={(e) => set({ max_session_minutes: e.target.value })}
            />
          </div>
          <div>
            <Label>Budget mensile chat vocale (USD, opzionale)</Label>
            <Input
              type="number"
              step="0.01"
              className="mt-1"
              value={value.monthly_budget_usd}
              disabled={disabled}
              onChange={(e) => set({ monthly_budget_usd: e.target.value })}
              placeholder="Nessun limite"
            />
          </div>
          <div>
            <Label>Minuti mensili per utente (opzionale)</Label>
            <Input
              type="number"
              min="1"
              className="mt-1"
              value={value.per_user_monthly_minutes}
              disabled={disabled}
              onChange={(e) => set({ per_user_monthly_minutes: e.target.value })}
              placeholder="Nessun limite"
            />
          </div>
          <div>
            <Label>Ragionamento (reasoning effort)</Label>
            <Select
              value={value.reasoning_effort || "__default__"}
              onValueChange={(v) => set({ reasoning_effort: v === "__default__" ? "" : v })}
              disabled={disabled}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Predefinito del modello</SelectItem>
                {effortOptions.map((e) => (
                  <SelectItem key={e} value={e}>{EFFORT_LABELS[e] || e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/50 p-4">
          <div>
            <Label>Mostra costo all&apos;utente (test)</Label>
            <p className="text-xs text-slate-500">
              Contatore live del costo nella chat vocale e riepilogo a fine sessione. Da disattivare finita la fase di test.
            </p>
          </div>
          <Switch
            checked={value.show_cost_to_user}
            disabled={disabled}
            onCheckedChange={(v) => set({ show_cost_to_user: v })}
          />
        </div>

        <div className="space-y-2">
          <div>
            <Label>Tariffe per modello (USD per 1M token)</Label>
            <p className="text-xs text-slate-500 mt-0.5">
              Usate per calcolare il costo di ogni sessione. Il valore viene fissato all&apos;avvio della sessione.
            </p>
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left font-medium px-2 py-2">Modello</th>
                  {PRICING_COLUMNS.map((c) => (
                    <th key={c.key} className="text-right font-medium px-2 py-2 whitespace-nowrap">{c.label}</th>
                  ))}
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {pricingModels.map((model) => (
                  <tr key={model} className="border-t">
                    <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                      {model}
                      {model === value.model && <Badge variant="outline" className="ml-1.5 text-[9px]">in uso</Badge>}
                    </td>
                    {PRICING_COLUMNS.map((c) => (
                      <td key={c.key} className="px-1 py-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="h-8 w-20 text-right text-xs ml-auto"
                          value={value.pricing[model]?.[c.key] ?? ""}
                          disabled={disabled}
                          onChange={(e) => setRate(model, c.key, e.target.value)}
                        />
                      </td>
                    ))}
                    <td className="px-1">
                      {!BUILTIN_REALTIME_MODELS.includes(model) && model !== value.model && (
                        <button
                          type="button"
                          onClick={() => removeModel(model)}
                          className="p-1 text-slate-400 hover:text-rose-600"
                          aria-label={`Rimuovi tariffe ${model}`}
                          disabled={disabled}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-56 font-mono text-xs"
              placeholder="Nuovo modello (id)"
              value={newModel}
              disabled={disabled}
              onChange={(e) => setNewModel(e.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled || !newModel.trim()}
              onClick={() => {
                addModel(newModel);
                setNewModel("");
              }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Aggiungi tariffe
            </Button>
            {customMode && value.model && !pricingModels.includes(value.model) && (
              <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => addModel(value.model)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Aggiungi «{value.model}»
              </Button>
            )}
          </div>
        </div>
        {off && !disabled && (
          <p className="text-xs text-slate-400">La chat vocale è disattivata: le impostazioni vengono salvate ma non usate.</p>
        )}
    </AiConfigCollapsibleCard>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div className="rounded-lg bg-slate-50 p-4 text-center">
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function SessionDetail({ sessionId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/admin/assistant-voice/sessions/${sessionId}`, { headers: headers() });
        if (res.ok && !cancelled) setDetail(await res.json());
      } catch {
        toast.error("Errore caricamento dettaglio sessione");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (loading) {
    return <div className="py-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>;
  }
  if (!detail) return <p className="text-xs text-slate-500 py-2">Dettaglio non disponibile.</p>;

  const usage = detail.usage || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 py-3 text-xs">
      <div>
        <p className="font-semibold text-slate-600 mb-1.5">Consumi</p>
        <ul className="space-y-0.5 text-slate-600">
          {PRICING_COLUMNS.map((c) => (
            <li key={c.key} className="flex justify-between gap-2">
              <span>{c.label}</span>
              <span className="tabular-nums">{(usage[c.key] || 0).toLocaleString("it-IT")}</span>
            </li>
          ))}
          <li className="flex justify-between gap-2 border-t pt-1 mt-1 font-medium">
            <span>Risposte</span>
            <span>{detail.responses || 0}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Fine</span>
            <span>{END_REASON_LABELS[detail.end_reason] || detail.end_reason || "—"}</span>
          </li>
          <li className="flex justify-between gap-2">
            <span>Voce</span>
            <span>{detail.voice || "—"}</span>
          </li>
        </ul>
      </div>
      <div>
        <p className="font-semibold text-slate-600 mb-1.5">Strumenti chiamati ({detail.tools_used?.length || 0})</p>
        {detail.tools_used?.length ? (
          <ul className="space-y-0.5 max-h-48 overflow-y-auto pr-1">
            {detail.tools_used.map((t, i) => (
              <li key={`${t.tool}-${i}`} className="flex justify-between gap-2 text-slate-600">
                <span className="font-mono truncate">{t.tool}</span>
                <span className={t.pending ? "text-amber-600" : t.success ? "text-emerald-600" : "text-rose-600"}>
                  {t.pending ? "conferma" : t.success ? "ok" : "errore"}
                </span>
              </li>
            ))}
          </ul>
        ) : <p className="text-slate-400">Nessuno</p>}
      </div>
      <div>
        <p className="font-semibold text-slate-600 mb-1.5">Azioni proposte ({detail.actions?.length || 0})</p>
        {detail.actions?.length ? (
          <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {detail.actions.map((a) => (
              <li key={a.id} className="rounded border border-slate-200 bg-white px-2 py-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-slate-700">{a.riepilogo || a.label}</span>
                  <Badge variant="outline" className="text-[9px] shrink-0">
                    {ACTION_STATUS_LABELS[a.status] || a.status}
                  </Badge>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {a.tool}
                  {a.confirmed_via ? ` · confermata via ${a.confirmed_via === "button" ? "pulsante" : "voce"}` : ""}
                  {a.executed_at ? ` · ${fmtWhen(a.executed_at)}` : ""}
                </p>
                {a.result_summary && (
                  <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{a.result_summary}</p>
                )}
              </li>
            ))}
          </ul>
        ) : <p className="text-slate-400">Nessuna</p>}
      </div>
    </div>
  );
}

/** Tab «Chat vocale» in Admin → Intelligenza artificiale: indicatori, utenti, sessioni. */
export default function AssistantVoiceAdminTab() {
  const [month, setMonth] = useState(currentMonth());
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState({ items: [], total: 0, limit: 50, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [togglingUser, setTogglingUser] = useState(null);
  const [terminatingId, setTerminatingId] = useState(null);

  const loadSessions = useCallback(async (offset = 0) => {
    setSessionsLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50", offset: String(offset) });
      if (month) params.set("month", month);
      if (userFilter) params.set("user_id", userFilter);
      if (statusFilter) params.set("status", statusFilter);
      const res = await fetch(`${API}/api/admin/assistant-voice/sessions?${params}`, { headers: headers() });
      if (res.ok) setSessions(await res.json());
    } catch {
      toast.error("Errore caricamento sessioni vocali");
    } finally {
      setSessionsLoading(false);
    }
  }, [month, userFilter, statusFilter]);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const qs = month ? `?month=${encodeURIComponent(month)}` : "";
      const [sRes, uRes] = await Promise.all([
        fetch(`${API}/api/admin/assistant-voice/summary${qs}`, { headers: headers() }),
        fetch(`${API}/api/admin/assistant-voice/users${qs}`, { headers: headers() }),
      ]);
      if (sRes.ok) setSummary(await sRes.json());
      if (uRes.ok) setUsers((await uRes.json()).items || []);
    } catch {
      toast.error("Errore caricamento monitoraggio chat vocale");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadSessions(0); }, [loadSessions]);

  const refresh = () => {
    loadOverview();
    loadSessions(sessions.offset || 0);
  };

  const toggleUser = async (user, enabled) => {
    setTogglingUser(user.user_id);
    try {
      const res = await fetch(`${API}/api/admin/users/${user.user_id}/assistant-voice`, {
        method: "PATCH",
        headers: headers(true),
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Errore aggiornamento utente");
      toast.success(data.message || "Aggiornato");
      setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? { ...u, assistant_voice_enabled: enabled } : u)));
      if (!enabled) loadSessions(sessions.offset || 0);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingUser(null);
    }
  };

  const terminate = async (session) => {
    setTerminatingId(session.id);
    try {
      const res = await fetch(`${API}/api/admin/assistant-voice/sessions/${session.id}/terminate`, {
        method: "POST",
        headers: headers(),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Errore");
      toast.success("Sessione terminata");
      refresh();
    } catch (err) {
      toast.error(err.message || "Impossibile terminare la sessione");
    } finally {
      setTerminatingId(null);
    }
  };

  const userOptions = useMemo(
    () => users.map((u) => ({ id: u.user_id, label: u.user_name || u.user_email || u.user_id })),
    [users]
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <AudioLines className="w-5 h-5 text-teal-600" />
            Chat vocale: utilizzo e costi
          </CardTitle>
          <p className="text-sm text-slate-500">
            Sessioni vocali dell&apos;assistente (OpenAI Realtime). Il costo è calcolato dai consumi ufficiali di ogni risposta.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div>
              <Label>Mese</Label>
              <Input type="month" className="mt-1 w-44" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <Button variant="outline" onClick={refresh} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Aggiorna
            </Button>
          </div>

          {summary ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <Kpi label="Sessioni" value={summary.sessions} />
                <Kpi label="Minuti" value={summary.minutes?.toLocaleString("it-IT")} />
                <Kpi label="Costo" value={fmtUsd(summary.cost_usd, 2)} hint={fmtUsd(summary.cost_usd, 4)} />
                <Kpi label="Costo medio / minuto" value={fmtUsd(summary.avg_cost_per_minute, 4)} />
                <Kpi label="Utenti attivi" value={summary.active_users} hint={`${summary.enabled_users} abilitati`} />
                <Kpi label="Sessioni in corso" value={summary.active_sessions} hint={`${summary.actions_executed} azioni eseguite`} />
              </div>
              {summary.by_model?.length > 0 && (
                <ul className="mt-4 text-sm space-y-1">
                  {summary.by_model.map((row) => (
                    <li key={row.model || "?"} className="flex justify-between text-slate-600">
                      <span className="font-mono">{row.model || "—"}</span>
                      <span>{row.sessions} sessioni · {row.minutes} min · {fmtUsd(row.cost_usd)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : (
            <p className="text-sm text-slate-500">Nessun dato disponibile.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Utenti</CardTitle>
          <p className="text-sm text-slate-500">
            Utenti abilitati e utenti con consumi nel mese. Per abilitare un nuovo utente apri <strong>Utenti</strong> → dettaglio
            utente → <strong>Assistente vocale</strong>.
          </p>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun utente abilitato alla chat vocale.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr>
                    <th className="text-left font-medium py-2 pr-3">Utente</th>
                    <th className="text-right font-medium py-2 px-2">Sessioni</th>
                    <th className="text-right font-medium py-2 px-2">Minuti</th>
                    <th className="text-right font-medium py-2 px-2">Token audio</th>
                    <th className="text-right font-medium py-2 px-2">Token testo</th>
                    <th className="text-right font-medium py-2 px-2">Costo</th>
                    <th className="text-left font-medium py-2 px-2">Ultimo uso</th>
                    <th className="text-center font-medium py-2 pl-2">Abilitato</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b last:border-0">
                      <td className="py-2 pr-3">
                        <p className="font-medium text-slate-800">{u.user_name || "—"}</p>
                        <p className="text-xs text-slate-500">{u.user_email}{u.facility_name ? ` · ${u.facility_name}` : ""}</p>
                      </td>
                      <td className="text-right px-2 tabular-nums">{u.sessions}</td>
                      <td className="text-right px-2 tabular-nums">{u.minutes}</td>
                      <td className="text-right px-2 tabular-nums">{u.audio_tokens.toLocaleString("it-IT")}</td>
                      <td className="text-right px-2 tabular-nums">{u.text_tokens.toLocaleString("it-IT")}</td>
                      <td className="text-right px-2 tabular-nums">{fmtUsd(u.cost_usd)}</td>
                      <td className="px-2 text-xs text-slate-500">{fmtWhen(u.last_used)}</td>
                      <td className="text-center pl-2">
                        <Switch
                          checked={u.assistant_voice_enabled}
                          disabled={togglingUser === u.user_id}
                          onCheckedChange={(v) => toggleUser(u, v)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sessioni</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div>
              <Label>Utente</Label>
              <Select value={userFilter || "__all__"} onValueChange={(v) => setUserFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="mt-1 w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutti gli utenti</SelectItem>
                  {userOptions.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stato</Label>
              <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
                <SelectTrigger className="mt-1 w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tutte</SelectItem>
                  <SelectItem value="active">In corso</SelectItem>
                  <SelectItem value="ended">Terminate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {sessionsLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 mb-2.5" />}
          </div>

          {sessions.items.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna sessione nel periodo selezionato.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr>
                    <th className="text-left font-medium py-2 pr-3">Inizio</th>
                    <th className="text-left font-medium py-2 px-2">Utente</th>
                    <th className="text-left font-medium py-2 px-2">Modello</th>
                    <th className="text-right font-medium py-2 px-2">Durata</th>
                    <th className="text-right font-medium py-2 px-2">Tool</th>
                    <th className="text-right font-medium py-2 px-2">Azioni</th>
                    <th className="text-right font-medium py-2 px-2">Costo</th>
                    <th className="text-left font-medium py-2 px-2">Stato</th>
                    <th className="py-2 pl-2" />
                  </tr>
                </thead>
                <tbody>
                  {sessions.items.map((s) => (
                    <Fragment key={s.id}>
                      <tr className="border-b last:border-0">
                        <td className="py-2 pr-3 text-xs text-slate-600 whitespace-nowrap">{fmtWhen(s.started_at)}</td>
                        <td className="px-2">
                          <p className="text-slate-800">{s.user_name || "—"}</p>
                          <p className="text-xs text-slate-500">{s.organization_name || s.user_email}</p>
                        </td>
                        <td className="px-2 font-mono text-xs">{s.model}</td>
                        <td className="text-right px-2 tabular-nums">{fmtDuration(s.duration_seconds)}</td>
                        <td className="text-right px-2 tabular-nums">{s.tool_calls || 0}</td>
                        <td className="text-right px-2 tabular-nums">{s.actions_executed || 0}</td>
                        <td className="text-right px-2 tabular-nums">{fmtUsd(s.cost_usd)}</td>
                        <td className="px-2">
                          {s.status === "active" ? (
                            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">In corso</Badge>
                          ) : (
                            <span className="text-xs text-slate-500">{END_REASON_LABELS[s.end_reason] || "Terminata"}</span>
                          )}
                        </td>
                        <td className="pl-2 whitespace-nowrap text-right">
                          {s.status === "active" && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-rose-700 border-rose-200 hover:bg-rose-50 mr-1"
                              disabled={terminatingId === s.id}
                              onClick={() => terminate(s)}
                            >
                              {terminatingId === s.id
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <PhoneOff className="w-3.5 h-3.5 mr-1" />}
                              Termina
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7"
                            onClick={() => setExpandedId((id) => (id === s.id ? null : s.id))}
                            aria-label="Dettaglio sessione"
                          >
                            {expandedId === s.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                      {expandedId === s.id && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={9} className="px-3">
                            <SessionDetail sessionId={s.id} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {sessions.total > sessions.limit && (
            <div className="flex justify-between items-center mt-4 text-xs text-slate-500">
              <span>
                {sessions.offset + 1}–{Math.min(sessions.offset + sessions.limit, sessions.total)} di {sessions.total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={sessions.offset === 0 || sessionsLoading}
                  onClick={() => loadSessions(Math.max(0, sessions.offset - sessions.limit))}
                >
                  Precedenti
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={sessions.offset + sessions.limit >= sessions.total || sessionsLoading}
                  onClick={() => loadSessions(sessions.offset + sessions.limit)}
                >
                  Successive
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
